/**
 * Created by fers on 26.09.15.
 */

import { v4 as uuidv4 } from 'uuid';
import {declareEntity} from '../fw/MetaDataHelper.ts';
import * as Permission from '../../common/util/Permission';

export default declareEntity({
    entity: "instances",
    graphQL: "Instance",
    name: "Instance",
    dynamoPrefix: 'def',
    storageType: 'schema',
    isPublic: true,
    writePermission: [
        Permission.PERM_INSTANCE_ADMIN
    ],
    defaultTitle: (data) => {
        return data['name'];
    },
    fields: (types) => { return {
        id: {
            name: "Instance ID",
            type: "id",
            defaultValue: () => {
                return uuidv4();
            },
            primaryKey: true,
            required: true,
            userEditable: false,
        },
        name: {
            name: "Name",
            type: "string",
            translatable: true,
            defaultValue: "",
            isPublic: true,
        },
        projectName: {
            name: "Project Name",
            type: "string",
            translatable: true,
            defaultValue: "",
            isPublic: true,
        },
        supportEmail: {
            name: "Support Email",
            type: "string",
            translatable: true,
            defaultValue: "",
            isPublic: true,
        },
        supportUrl: {
            name: "Support URL",
            type: "string",
            translatable: true,
            defaultValue: "",
            isPublic: true,
        },
        database: {
            name: "Database",
            type: "relation",
            to: () => types['databases'],
            userEditable: false,
            permission: Permission.PERM_INSTANCE_ADMIN,
        },
        autoupgrade: {
            name: "Autoupgrade",
            type: "boolean",
            defaultValue: false,
            userEditable: false,
            permission: Permission.PERM_INSTANCE_ADMIN,
        },
        logo: {
            name: "Logo",
            type: "string",
            isPublic: true,
        },
        authorization: {
            name: "Authorization",
            type: "enum",
            defaultValue: "dativery",
            isPublic: true,
            values: {
                "dativery": {
                    name: "Dativery",
                },
                "jwt": {
                    name: "JWT",
                },
                "none": {
                    name: "None (public only)",
                },
            },
            permission: Permission.PERM_INSTANCE_ADMIN,
        },
        showScenarios: {
            name: "Show scenarios",
            type: "enum",
            defaultValue: "all",
            isPublic: true,
            values: {
                "all": {
                    name: "All accessible in Dativery",
                },
                "selected": {
                    name: "Selected only",
                },
            }
        },
        jwt: {
            name: "JWT",
            type: "string",
            isPublic: false,
            writeOnly: true,
            visibility: (data) => data['authorization'] === "jwt",
        },
        company: {
            name: "Company",
            translatable: true,
            type: "string",
            defaultValue: "",
            isPublic: true,
        },
        consultantUserIds: {
            name: "Consultant Users",
            translatable: false,
            type: "text",
            defaultValue: "",
            isPublic: false,
            permission: Permission.PERM_INSTANCE_ADMIN,
        },
        jobStats: {
            name: "Job Stats",
            type: "object",
            userEditable: false,
            export: false,
            import: false,
            controllerFn: "getJobStats",
            permission: Permission.PERM_INSTANCE_ADMIN,
        },
        primaryChannelDef: {
            name: "Primary application",
            type: "relation",
            to: () => types['channelDefs'],
        },
        updatedAt: {
            name: "Updated At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        },
        createdAt: {
            name: "Created At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        },
    }; },
    relations: (types) => { return {
        instanceScenarios: {
            type: 'oneToMany',
            to: types['instanceScenarios'],
            foreignKeys: [
                {
                    from: "id",
                    to: "instance_id"
                }
            ]
        },
        announcements: {
            type: 'oneToMany',
            to: types['announcements'],
            foreignKeys: [
                {
                    from: "id",
                    to: "instance_id"
                }
            ]
        },
        fetchStats: {
            type: 'manyToOne',
            to: types['fetchStats'],
            args: {
                channel_id: {
                    type: "string"
                },
                channelDef_id: {
                    type: "string"
                },
                flow_id: {
                    type: "string"
                },
                flowDef_id: {
                    type: "string"
                },
                organisation_id: {
                    type: "string"
                },
                sinceDate: {
                    type: "datetime"
                },
                frequency: {
                    type: "string"
                },
                since: {
                    type: "datetime"
                },
                to: {
                    type: "datetime"
                },
                period: {
                    type: "string"
                },
            },
            shouldFollow: false,
            controllerFn: 'getFetchStats',
            permission: Permission.PERM_INSTANCE_ADMIN,
        },
        organisations: {
            type: 'oneToMany',
            to: types['organisations'],
            foreignKeys: [
                {
                    from: "id",
                    to: "instance_id"
                }
            ],
            permission: Permission.PERM_INSTANCE_ADMIN,
        },
        users: {
            type: 'oneToMany',
            to: types['users'],
            foreignKeys: [
                {
                    from: "id",
                    to: "instance_id"
                }
            ],
            permission: Permission.PERM_INSTANCE_ADMIN,
        },
        consultantUsers: {
            type: 'oneToMany',
            to: types['users'],
            shouldFollow: false,
            controllerFn: 'getConsultantUsers',
        },
        flows: {
            type: 'oneToMany',
            to: types['flows'],
            controllerFn: 'getFilteredFlows',
            foreignKeys: [
                {
                    from: "id",
                    to: "instance_id",
                    asFilterOnly: true,
                }
            ],
            args: {
                /* 'none' = organisation has no consultant, empty/undefined = no filtering */
                consultant: {
                    type: "string",
                },
                author_id: {
                    type: "string",
                },
                unresolved: {
                    type: "boolean",
                },
                /* 'banks' | 'nonbanks', empty/undefined = no filtering */
                bankFilter: {
                    type: "string",
                },
                hasTransfersOrBanks: {
                    type: "boolean",
                },
            },
            permission: Permission.PERM_INSTANCE_ADMIN,
        },
        flowsSpecialPrices: {
            type: 'oneToMany',
            to: types['flows'],
            shouldFollow: false,
            controllerFn: 'getFlowsSpecialPrices',
            permission: Permission.PERM_INSTANCE_ADMIN,
        },
        channelsSpecialPrices: {
            type: 'oneToMany',
            to: types['channels'],
            shouldFollow: false,
            controllerFn: 'getChannelsSpecialPrices',
            permission: Permission.PERM_INSTANCE_ADMIN,
        },
        runningJobs: {
            type: 'oneToMany',
            to: types['runningJobs'],
            foreignKeys: [
                {
                    from: "id",
                    to: "instance_id"
                }
            ],
            permission: Permission.PERM_INSTANCE_ADMIN,
        },
        fetches: {
            type: 'oneToMany',
            to: types['fetches'],
            foreignKeys: [
                {
                    from: "id",
                    to: "instance_id"
                }
            ],
            permission: Permission.PERM_INSTANCE_ADMIN,
        },
        channels: {
            type: 'oneToMany',
            to: types['channels'],
            foreignKeys: [
                {
                    from: "id",
                    to: "instance_id"
                }
            ],
            permission: Permission.PERM_INSTANCE_ADMIN,
        },
        overdueBillActions: {
            type: 'oneToMany',
            to: types['organisationOverdueBillActions'],
            foreignKeys: [
                {
                    from: "id",
                    to: "instance_id"
                }
            ],
            args: {
                isRule: { type: 'boolean' },
                enabled: { type: 'boolean' },
                overdueDaysCount: { type: 'integer' },
                type: { type: 'string' },
                orderBy: { type: 'string' },
            },
            permission: Permission.PERM_INSTANCE_ADMIN,
        },
        overdueBillsSettings: {
            type: 'oneToMany',
            to: types['overdueBillsSettings'],
            foreignKeys: [
                {
                    from: 'id',
                    to: 'instance_id',
                }
            ],
            permission: Permission.PERM_INSTANCE_ADMIN,
        },
    }; }
});
